import { EntityTemplateVariables, TemplateVariable } from '@tools/interfaces/template';
import { getContactFieldsForEntity } from './template-contact';
import { getAccountingJournalFieldsForEntity } from './template-accounting-journal';
import { getPosSessionFieldsForEntity } from './template-pos-session';
import { getPriceCategoryFieldsForEntity } from './template-price-category';
import { templateOrder, templateOrderItem } from './template-order-and-order-items';
import { templatePayment } from './template-payment';

export const templateInvoice: EntityTemplateVariables = {
  fields: [
    {
      label: 'templates.invoices.code.label',
      contentValue: '{{ invoice.code }}',
      description: 'templates.invoices.code.description',
    },
    {
      label: 'templates.invoices.invoice_date.label',
      contentValue: '{{ invoice.invoice_date | date("d/m/Y") }}',
      description: 'templates.invoices.invoice_date.description',
    },
    {
      label: 'templates.invoices.due_date.label',
      contentValue: '{{ invoice.due_date | date("d/m/Y") }}',
      description: 'templates.invoices.due_date.description',
    },
    {
      label: 'templates.invoices.total_amount.label',
      contentValue: '{{ invoice.total_amount }}',
      description: 'templates.invoices.total_amount.description',
    },
    {
      label: 'templates.invoices.paid_amount.label',
      contentValue: '{{ invoice.paid_amount }}',
      description: 'templates.invoices.paid_amount.description',
    },
    {
      label: 'templates.invoices.balance.label',
      contentValue: '{{ invoice.balance }}',
      description: 'templates.invoices.balance.description',
    },
    {
      label: 'templates.invoices.status.label',
      contentValue: '{{ invoice.status }}',
      description: 'templates.invoices.status.description',
    },
    {
      label: 'templates.invoices.total_tax.label',
      contentValue: '{{ invoice.total_tax }}',
      description: 'templates.invoices.total_tax.description',
    },
    {
      label: 'templates.invoices.contact_data.label',
      contentValue: '{{ invoice.contact_data }}',
      description: 'templates.invoices.contact_data.description',
    },
    {
      label: 'templates.invoices.center_data.label',
      contentValue: '{{ invoice.center_data }}',
      description: 'templates.invoices.center_data.description',
    },
    {
      label: 'templates.invoices.footer_text.label',
      contentValue: '{{ invoice.footer_text }}',
      description: 'templates.invoices.footer_text.description',
    },
    {
      label: 'templates.invoices.invoice_type.label',
      contentValue: '{{ invoice.invoice_type }}',
      description: 'templates.invoices.invoice_type.description',
    },
    {
      label: 'templates.invoices.document_number.label',
      contentValue: '{{ invoice.document_number }}',
      description: 'templates.invoices.invoice_number.description',
    },
    {
      label: 'templates.invoices.invoice_prefix.label',
      contentValue: '{{ invoice.invoice_prefix }}',
      description: 'templates.invoices.invoice_prefix.description',
    },
    {
      label: 'templates.invoices.accounting_journal_code.label',
      contentValue: '{{ invoice.accounting_journal_code }}',
      description: 'templates.invoices.accounting_journal_code.description',
    },
    {
      label: 'templates.invoices.invoice_layout.label',
      contentValue: '{{ invoice.invoice_layout }}',
      description: 'templates.invoices.invoice_layout.description',
    },
    {
      label: 'templates.invoices.structured_communication.label',
      contentValue: '{{ invoice.structured_communication }}',
      description: 'templates.invoices.structured_communication.description',
    },
    {
      label: 'templates.invoices.invoice_period_start.label',
      contentValue: '{{ invoice.invoice_period_start | date("d/m/Y") }}',
      description: 'templates.invoices.invoice_period_start.description',
    },
    {
      label: 'templates.invoices.invoice_period_end.label',
      contentValue: '{{ invoice.invoice_period_end | date("d/m/Y") }}',
      description: 'templates.invoices.invoice_period_end.description',
    },
    {
      label: 'templates.invoices.paid_at.label',
      contentValue: '{{ invoice.paid_at }}',
      description: 'templates.invoices.paid_at.description',
    },

    {
      label: 'templates.invoices.invoice_link.label',
      contentValue: '{{ invoice.invoice_link }}',
      description: 'templates.invoices.invoice_link.description',
    },
    {
      label: 'templates.invoices.last_payment_date.label',
      contentValue: '{{ invoice.last_payment_date | date("d/m/Y") }}',
      description: 'templates.invoices.last_payment_date.description',
    },
    {
      label: 'templates.invoices.printable_structured_communication.label',
      contentValue: '{{ invoice.printable_structured_communication }}',
      description: 'templates.invoices.printable_structured_communication.description',
    },
    {
      label: 'templates.invoices.footer_for_structured_communication.label',
      contentValue: '{{ invoice.footer_for_structured_communication }}',
      description: 'templates.invoices.footer_for_structured_communication.description',
    },
  ],
  related: [
    {
      relationName: 'contact',
      relationType: 'simple',
      label: 'templates.invoices.contact.label',
      description: 'templates.invoices.contact.description',
      fields: getContactFieldsForEntity('invoice.'),
    },
    {
      relationName: 'accountingJournal',
      relationType: 'simple',
      label: 'templates.invoices.accounting_journal.label',
      description: 'templates.invoices.accounting_journal.description',
      fields: getAccountingJournalFieldsForEntity('invoice.'),
    },
    {
      relationName: 'posSession',
      relationType: 'simple',
      label: 'templates.invoices.pos_session.label',
      description: 'templates.invoices.pos_session.description',
      fields: getPosSessionFieldsForEntity('invoice.'),
    },
    {
      relationName: 'priceCategory',
      relationType: 'simple',
      label: 'templates.invoices.price_category.label',
      description: 'templates.invoices.price_category.description',
      fields: getPriceCategoryFieldsForEntity('invoice.'),
    },
  ],
  relatedInLoop: [
    {
      relationName: 'Orders',
      relationType: 'inLoop',
      label: 'templates.invoices.orders.label',
      description: 'templates.invoices.orders.description',
      blockStartValue: '{% for order in invoice.orders %}',
      contentValue: '{# Add some order fields #}',
      blockEndValue: '{% endfor %}',
      fields: templateOrder.fields,
    },
    {
      relationName: 'orderItems',
      relationType: 'inLoop',
      label: 'templates.invoices.order_items.label',
      description: 'templates.invoices.order_items.description',
      blockStartValue: '{% for order in invoice.orders %}\n{% for orderItem in order.orderItems %}',
      contentValue: '{# Add some orderItem fields #}',
      blockEndValue: '{% endfor %}\n{% endfor %}',
      fields: templateOrderItem.fields,
    },
    {
      relationName: 'payments',
      relationType: 'inLoop',
      label: 'templates.invoices.payments.label',
      description: 'templates.invoices.payments.description',
      blockStartValue: '{% for payment in invoice.payments %}',
      contentValue: '{# Add some payment fields #}',
      blockEndValue: '{% endfor %}',
      fields: templatePayment.fields,
    },
  ],
};

export const getUnpaidInvoiceFieldsForEntity = (entity: string): TemplateVariable[] => {
  return [
    {
      label: 'templates.invoices.code.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "code") | raw }}`,
      description: 'templates.invoices.code.description',
    },
    {
      label: 'templates.invoices.invoice_date.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "invoice_date") | date("d/m/Y") }}`,
      description: 'templates.invoices.invoice_date.description',
    },
    {
      label: 'templates.invoices.due_date.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "due_date") | date("d/m/Y") }}`,
      description: 'templates.invoices.due_date.description',
    },
    {
      label: 'templates.invoices.total_amount.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "total_amount") | raw }}`,
      description: 'templates.invoices.total_amount.description',
    },
    {
      label: 'templates.invoices.paid_amount.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "paid_amount") | raw }}`,
      description: 'templates.invoices.paid_amount.description',
    },
    {
      label: 'templates.invoices.balance.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "balance") | raw }}`,
      description: 'templates.invoices.balance.description',
    },
    {
      label: 'templates.invoices.status.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "status") | raw }}`,
      description: 'templates.invoices.status.description',
    },
    {
      label: 'templates.invoices.total_tax.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "total_tax") | raw }}`,
      description: 'templates.invoices.total_tax.description',
    },
    {
      label: 'templates.invoices.contact_data.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "contact_data") | raw }}`,
      description: 'templates.invoices.contact_data.description',
    },
    {
      label: 'templates.invoices.center_data.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "center_data") | raw }}`,
      description: 'templates.invoices.center_data.description',
    },
    {
      label: 'templates.invoices.footer_text.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "footer_text") | raw }}`,
      description: 'templates.invoices.footer_text.description',
    },
    {
      label: 'templates.invoices.invoice_type.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "invoice_type") | raw }}`,
      description: 'templates.invoices.invoice_type.description',
    },
    {
      label: 'templates.invoices.document_number.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "document_number") | raw }}`,
      description: 'templates.invoices.document_number.description',
    },
    {
      label: 'templates.invoices.invoice_prefix.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "invoice_prefix") | raw }}`,
      description: 'templates.invoices.invoice_prefix.description',
    },
    {
      label: 'templates.invoices.accounting_journal_code.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "accounting_journal_code") | raw }}`,
      description: 'templates.invoices.accounting_journal_code.description',
    },
    {
      label: 'templates.invoices.structured_communication.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "structured_communication") | raw }}`,
      description: 'templates.invoices.structured_communication.description',
    },
    {
      label: 'templates.invoices.invoice_period_start.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "invoice_period_start") | date("d/m/Y") }}`,
      description: 'templates.invoices.invoice_period_start.description',
    },
    {
      label: 'templates.invoices.invoice_period_end.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "invoice_period_end") | date("d/m/Y") }}`,
      description: 'templates.invoices.invoice_period_end.description',
    },
    {
      label: 'templates.invoices.invoice_link.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "invoice_link") | raw }}`,
      description: 'templates.invoices.invoice_link.description',
    },
    {
      label: 'templates.invoices.last_payment_date.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "last_payment_date") | date("d/m/Y") }}`,
      description: 'templates.invoices.last_payment_date.description',
    },
    {
      label: 'templates.invoices.printable_structured_communication.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "printable_structured_communication") | raw }}`,
      description: 'templates.invoices.printable_structured_communication.description',
    },
    {
      label: 'templates.invoices.footer_for_structured_communication.label',
      contentValue: `{{ templateHelper.getProductSinglePendingInvoiceFieldData(${entity}, "footer_for_structured_communication") | raw }}`,
      description: 'templates.invoices.footer_for_structured_communication.description',
    },
  ];
};
